/**
 * Message processor for AskAsper chat
 * Consumes SSE payloads and API history into AgenticChatMessage shape
 * consumed by AgenticTwoPanelLayout (messages + loadingMessage).
 */

import type { AgenticChatMessage, AgenticIntermediateStep, SSERawPayload, ApiHistoryMessage } from '../types';
import { extractClarification } from './messageProcessorClarification';
import { normalizeClarificationResumeDisplay } from '../utils/clarificationResumeMessage';
import {
    chartsOrderedChartsFirstThenTables,
    replaceLoadingPlaceholderWithCharts,
    updateTaskLoadingState,
} from './messageProcessorCharts';

/**
 * Create the initial loading (streaming) message for a new user question.
 * This is the message that gets updated by SSE and then moved to messages on stream_complete.
 */
export function createStreamingPlaceholder(question: string, threadId: string, runId?: string): AgenticChatMessage {
    const now = new Date().toISOString();
    return {
        id: `streaming-${Date.now()}`,
        thread_id: threadId,
        run_id: runId,
        type: 'ai',
        user_input: question,
        question,
        content: '',
        created_on: now,
        additional_kwargs: {
            intermediate_steps: [],
        },
        charts: [],
    };
}

/**
 * Merge or replace an intermediate step in the list.
 * If there is already an "executing" step for the same step name, replace it; otherwise append.
 */
function mergeIntermediateStep(
    steps: AgenticIntermediateStep[],
    incoming: AgenticIntermediateStep,
): AgenticIntermediateStep[] {
    const idx = steps.findIndex((s) => s.step === incoming.step && s.status === 'executing');
    if (idx !== -1) {
        const next = [...steps];
        next[idx] = incoming;
        return next;
    }
    return [...steps, incoming];
}

/**
 * Update the loading message with one SSE event.
 * Returns a new AgenticChatMessage; does not mutate.
 */
export function updateLoadingMessage(loadingMessage: AgenticChatMessage, json: SSERawPayload): AgenticChatMessage {
    switch (json.type) {
        case 'intermediate_step': {
            const steps = loadingMessage.additional_kwargs?.intermediate_steps ?? [];
            const updated = mergeIntermediateStep(steps, json.step);
            return {
                ...loadingMessage,
                run_id: json.run_id,
                additional_kwargs: {
                    ...loadingMessage.additional_kwargs,
                    intermediate_steps: updated,
                },
            };
        }

        case 'visualization': {
            const charts = loadingMessage.charts ?? [];
            const newCharts = (json.charts ?? []).map((c) => ({
                viz_data: c.viz_data,
                task_id: c.task_id ?? json.task_id,
                task_name: c.task_name ?? json.task_name,
            }));
            return {
                ...loadingMessage,
                run_id: json.run_id,
                charts: chartsOrderedChartsFirstThenTables([...charts, ...newCharts]),
            };
        }

        case 'task_start': {
            const tasks = loadingMessage.additional_kwargs?.tasks ?? [];
            const existingIdx = tasks.findIndex((t) => t.task_id === json.task_id);
            const newTask = {
                task_id: json.task_id,
                task_name: json.task_name,
                status: 'executing' as const,
                content: json.content,
            };
            const updatedTasks =
                existingIdx >= 0 ? tasks.map((t, i) => (i === existingIdx ? newTask : t)) : [...tasks, newTask];
            return {
                ...loadingMessage,
                run_id: json.run_id,
                additional_kwargs: {
                    ...loadingMessage.additional_kwargs,
                    tasks: updatedTasks,
                },
            };
        }

        case 'task_narrative_start': {
            return updateTaskLoadingState(loadingMessage, json, 'narrative_loading', true);
        }

        case 'task_narrative_end': {
            const updated = updateTaskLoadingState(loadingMessage, json, 'narrative_loading', false);
            const tasks = updated.additional_kwargs?.tasks ?? [];
            const existingIdx = tasks.findIndex((t) => t.task_id === json.task_id);
            if (existingIdx >= 0 && json.narrative_output) {
                const task = tasks[existingIdx];
                const updatedTasks = tasks.map((t, i) =>
                    i === existingIdx
                        ? {
                              ...t,
                              task_output: {
                                  ...(typeof t.task_output === 'object' && t.task_output != null
                                      ? (t.task_output as Record<string, unknown>)
                                      : {}),
                                  result: {
                                      ...(typeof (t.task_output as any)?.result === 'object' &&
                                      (t.task_output as any)?.result != null
                                          ? ((t.task_output as any).result as Record<string, unknown>)
                                          : {}),
                                      ...json.narrative_output,
                                  },
                              },
                          }
                        : t
                );
                return {
                    ...updated,
                    additional_kwargs: { ...updated.additional_kwargs, tasks: updatedTasks },
                };
            }
            return updated;
        }

        case 'table_generation_start': {
            const charts = loadingMessage.charts ?? [];
            const placeholder = {
                viz_data: { _loading: true, chart_type: 'table' },
                task_id: json.task_id,
                task_name: json.task_name,
            };
            return {
                ...updateTaskLoadingState(loadingMessage, json, 'table_loading', true),
                charts: chartsOrderedChartsFirstThenTables([...charts, placeholder]),
            };
        }

        case 'table_generation_end': {
            const charts = loadingMessage.charts ?? [];
            // API sends table_data as array [{viz_data, scenario_link}, ...] or single {viz_data}
            const tableData = json.table_data;
            const tableItems = Array.isArray(tableData)
                ? tableData
                : tableData?.viz_data
                  ? [{ viz_data: tableData.viz_data, scenario_link: (tableData as any).scenario_link }]
                  : [];
            const updatedCharts =
                tableItems.length > 0
                    ? replaceLoadingPlaceholderWithCharts(charts, json.task_id, 'table', tableItems, json.task_name)
                    : charts;
            return {
                ...updateTaskLoadingState(loadingMessage, json, 'table_loading', false),
                run_id: json.run_id,
                charts: chartsOrderedChartsFirstThenTables(updatedCharts),
            };
        }

        case 'viz_generation_start': {
            const charts = loadingMessage.charts ?? [];
            const placeholder = {
                viz_data: { _loading: true, chart_type: 'BarChart' },
                task_id: json.task_id,
                task_name: json.task_name,
            };
            return {
                ...updateTaskLoadingState(loadingMessage, json, 'viz_loading', true),
                charts: chartsOrderedChartsFirstThenTables([...charts, placeholder]),
            };
        }

        case 'viz_generation_end': {
            const charts = loadingMessage.charts ?? [];
            // API may send chart_data as array [{viz_data}, ...] or single {viz_data}
            const chartData = json.chart_data;
            const chartItems = Array.isArray(chartData)
                ? chartData
                : chartData?.viz_data
                  ? [{ viz_data: chartData.viz_data }]
                  : [];
            const updatedCharts =
                chartItems.length > 0
                    ? replaceLoadingPlaceholderWithCharts(
                          charts,
                          json.task_id,
                          chartItems[0].viz_data?.chart_type ?? 'BarChart',
                          chartItems,
                          json.task_name,
                      )
                    : charts;
            return {
                ...updateTaskLoadingState(loadingMessage, json, 'viz_loading', false),
                run_id: json.run_id,
                charts: chartsOrderedChartsFirstThenTables(updatedCharts),
            };
        }

        case 'task_complete': {
            const tasks = loadingMessage.additional_kwargs?.tasks ?? [];
            const existingIdx = tasks.findIndex((t) => t.task_id === json.task_id);
            const completedTask = {
                task_id: json.task_id,
                task_name: json.task_name,
                status: 'completed' as const,
                content: undefined,
                task_output: json.task_output,
            };
            const updatedTasks =
                existingIdx >= 0
                    ? tasks.map((t, i) => (i === existingIdx ? completedTask : t))
                    : [...tasks, completedTask];

            // During streaming: charts come only from table_generation_end and viz_generation_end.
            // Do not add charts from task_complete to avoid duplicates.
            // Loading/history uses task_complete_events (deriveLayoutFromMessages) as-is.
            return {
                ...loadingMessage,
                run_id: json.run_id,
                additional_kwargs: {
                    ...loadingMessage.additional_kwargs,
                    tasks: updatedTasks,
                },
            };
        }

        case 'final_output_starting': {
            return {
                ...loadingMessage,
                run_id: json.run_id,
            };
        }

        case 'ai': {
            const kwargs = json.additional_kwargs ?? {};
            const overview = json.overview ?? kwargs.overview;
            const key_takeaways = json.key_takeaways ?? kwargs.key_takeaways;
            const interrupt = kwargs.interrupt;
            // When interrupt=true, the ai event may be the first/only content carrier (clarification question).
            // Prefer incoming content when non-empty; avoid overwriting with empty (streaming chunks or early interrupt).
            const hasIncomingContent = typeof json.content === 'string' && json.content.trim().length > 0;
            const baseContent = hasIncomingContent ? json.content : (loadingMessage.content ?? json.content ?? '');

            const { content: clarifiedContent, fields: clarificationKwargs } = extractClarification(
                kwargs,
                baseContent,
            );
            const resolvedContent = clarifiedContent ?? '';

            return {
                ...loadingMessage,
                type: 'ai',
                content: resolvedContent,
                thread_id: json.thread_id ?? loadingMessage.thread_id,
                run_id: json.run_id,
                overview,
                key_takeaways,
                additional_kwargs: {
                    ...loadingMessage.additional_kwargs,
                    intermediate_steps:
                        kwargs.intermediate_steps ?? loadingMessage.additional_kwargs?.intermediate_steps,
                    followup_questions: kwargs.followup_questions,
                    interrupt,
                    interrupt_type:
                        (kwargs as { interrupt_type?: string }).interrupt_type ??
                        loadingMessage.additional_kwargs?.interrupt_type,
                    interrupt_count:
                        (kwargs as { interrupt_count?: number }).interrupt_count ??
                        loadingMessage.additional_kwargs?.interrupt_count,
                    ...clarificationKwargs,
                },
                answer: {
                    data: {
                        output: resolvedContent,
                        intermediate_steps:
                            kwargs.intermediate_steps ?? loadingMessage.additional_kwargs?.intermediate_steps,
                        followup_question: parseFollowupQuestions(kwargs.followup_questions),
                    },
                },
            };
        }

        case 'stream_complete': {
            return {
                ...loadingMessage,
                run_id: json.run_id,
            };
        }

        default:
            return loadingMessage;
    }
}

function parseFollowupQuestions(
    raw: string | undefined,
): Array<string | { keyword?: string; question?: string; text?: string }> | undefined {
    if (raw == null || raw === '') return undefined;
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
        return undefined;
    } catch {
        return undefined;
    }
}

/**
 * Transform an array of API history messages into AgenticChatMessage format.
 */
export function transformChatHistory(apiMessages: ApiHistoryMessage[]): AgenticChatMessage[] {
    return apiMessages.map(normalizeApiMessageToAgentic);
}

/**
 * Normalize a message from the past-messages/history API into AgenticChatMessage
 * so the two-panel layout can consume it.
 *
 * When the API returns task_complete_events but not tasks, we map task_complete_events
 * to tasks so the layout uses the primary path. This ensures phase card titles
 * (task_name) stay aligned with their overview/content - avoiding the swap bug
 * where Planning step order differs from task_complete_events order.
 */
export function normalizeApiMessageToAgentic(apiMsg: ApiHistoryMessage): AgenticChatMessage {
    // A disambiguation resume persists the ignore instruction sent to the API; show the same short
    // entity list the user saw live.
    const q = normalizeClarificationResumeDisplay(apiMsg.user_input ?? apiMsg.question ?? '');
    const kwargs = apiMsg.additional_kwargs ?? {};

    // Map task_complete_events → tasks when tasks is missing, so layout uses primary path
    // and phase card titles match their content (no index-based mismatch with Planning step)
    let tasks = kwargs.tasks;
    const taskCompleteEvents = kwargs.task_complete_events;
    if (
        (!Array.isArray(tasks) || tasks.length === 0) &&
        Array.isArray(taskCompleteEvents) &&
        taskCompleteEvents.length > 0
    ) {
        tasks = taskCompleteEvents.map(
            (evt: { task_id?: string; task_name?: string; task_output?: unknown }, i: number) => ({
                task_id: evt.task_id ?? `phase-${i}`,
                task_name: evt.task_name ?? `Task ${i + 1}`,
                status: 'completed' as const,
                task_output: evt.task_output,
            })
        );
    }

    // Prefer already-structured kwargs; otherwise parse raw interrupt content (legacy backends).
    const {
        content,
        contentRewritten,
        fields: clarificationFields,
    } = extractClarification(kwargs, apiMsg.content);

    const additional_kwargs = {
        ...kwargs,
        ...(Array.isArray(tasks) && tasks.length > 0 ? { tasks } : {}),
        ...clarificationFields,
    };

    // Only when the legacy parser replaced a dict-string content: keep answer.data.output (the
    // left-panel bubble source) in sync with the human message we extracted.
    let answer = apiMsg.answer;
    if (
        contentRewritten &&
        typeof content === 'string' &&
        answer?.data?.output != null &&
        answer.data.output !== content
    ) {
        answer = {
            ...answer,
            data: {
                ...answer.data,
                output: content,
            },
        };
    }

    return {
        id: apiMsg.id,
        thread_id: apiMsg.thread_id,
        run_id: apiMsg.run_id,
        type: apiMsg.type ?? 'ai',
        content,
        user_input: q,
        question: q,
        created_on: apiMsg.created_on,
        thumbs_up: apiMsg.thumbs_up,
        overview: apiMsg.overview,
        key_takeaways: apiMsg.key_takeaways,
        additional_kwargs,
        answer,
        charts: apiMsg.charts,
    };
}
